import { Analytics } from "@segment/analytics-next";
import { useContext, useEffect, useCallback } from "react";
import { SegmentContext } from "../pages/_app";
import { EnvData, Tenant, UserData } from "./types";
import useEnv from "./useEnv";
import useTenant from "./useTenant";
import { useOptionalUser } from "./useUser";

const DEBUG = !!process.env.NEXT_PUBLIC_TRACKING_DEBUG;

export function trackSegment(
  analytics: Analytics | undefined,
  message: string,
  tenant: Tenant | null,
  env: EnvData | null,
  user: UserData | null,
  args?: { [key: string]: unknown }
) {
  // Don't track actions in impersonation sessions.
  if (user?.impersonated_by) return;

  if (DEBUG) console.log("TRACKING", message, args);

  const properties = {
    ...args,
    environmentId: env?.pub_id,
    environmentName: env?.name,
    tenantId: tenant?.id,
    email: user?.email,
  };
  const options = { context: { app: { name: "ui" }, groupId: tenant?.id } };
  if (analytics) {
    analytics.track(message, properties, options);
  }
  return {
    unifyFakePageHack: (should?: boolean) => {
      if (should === false) return;
      let path = `/FAKE/${message.replaceAll(" ", "-")}`;
      if (args) {
        for (const arg of Object.keys(args).sort()) {
          path += `/${arg}/${args[arg]}`;
        }
      }
      if (DEBUG) console.log("EMITTING FAKE PAGE EVENT", path);
      if (analytics) {
        analytics.page(
          "FAKE",
          `FAKE PAGE FOR UNIFY - ${message}`,
          { ...properties, path },
          options
        );
      }
    },
  };
}

export function useSegment() {
  return useContext(SegmentContext);
}

// These Identify and Group calls are _load-bearing_ and not redundant with
// those in `control/src/segment.rs`. These calls update the Segment cookie
// that tracks user identity across UI, docs, and dot-com.
export function useSegmentIdentify() {
  const analytics = useSegment();
  const { user } = useOptionalUser();
  const { tenant } = useTenant();

  useEffect(() => {
    if (user && user.impersonated_by) return;

    if (analytics) {
      if (user)
        analytics.identify(
          user.id,
          { email: user.email, name: user.name },
          { context: { app: { name: "ui" } } }
        );

      if (tenant)
        analytics.group(tenant.id, {}, { context: { app: { name: "ui" } } });
    }
    if (DEBUG) console.log("TRACKING", "identify", user, tenant);
  }, [analytics, tenant, user]);
}

export function useSegmentCallback(message: string, args?: any) {
  const analytics = useSegment();
  const { selectedEnv: env } = useEnv();
  const { tenant } = useTenant();
  const { user } = useOptionalUser();

  return useCallback(
    () => trackSegment(analytics, message, tenant, env, user, args),
    [analytics, args, tenant, env, message]
  );
}

export function useSegmentCallbackV2() {
  const analytics = useSegment();
  const { selectedEnv: env } = useEnv();
  const { tenant } = useTenant();
  const { user } = useOptionalUser();

  return useCallback(
    (message: string, args?: any) =>
      trackSegment(analytics, message, tenant, env, user, args),
    [analytics, tenant, env]
  );
}
